/**
 * Theme Manager - Handles light/dark mode and brand theme switching
 *
 * Supports four theme combinations:
 * - Company Tracker Light (default)
 * - Company Tracker Dark
 * - Materiality Tracker Light
 * - Materiality Tracker Dark
 */

export type ThemeMode = 'light' | 'dark';
export type ThemeBrand = 'company' | 'materiality' | 'emissions';

const MODE_STORAGE_KEY = 'mode-preference';
const THEME_STORAGE_KEY = 'theme-preference';

export const getMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = localStorage.getItem(MODE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }

  return 'light';
};

export const setMode = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(MODE_STORAGE_KEY, mode);
  document.documentElement.setAttribute('data-mode', mode);
};

export const toggleMode = (): ThemeMode => {
  const currentMode = getMode();
  const newMode: ThemeMode = currentMode === 'light' ? 'dark' : 'light';
  setMode(newMode);
  return newMode;
};

export const getTheme = (): ThemeBrand => {
  if (typeof window === 'undefined') {
    return 'company';
  }

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'company' || stored === 'materiality' || stored === 'emissions') {
    return stored;
  }

  return 'company';
};

export const setTheme = (theme: ThemeBrand): void => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(THEME_STORAGE_KEY, theme);
  document.documentElement.setAttribute('data-theme', theme);
};

export const initializeTheme = (): { mode: ThemeMode; theme: ThemeBrand } => {
  const mode = getMode();
  const theme = getTheme();

  if (typeof window !== 'undefined') {
    document.documentElement.setAttribute('data-mode', mode);
    document.documentElement.setAttribute('data-theme', theme);
  }

  return { mode, theme };
};

export const clearModePreference = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(MODE_STORAGE_KEY);
  document.documentElement.removeAttribute('data-mode');
};
